// Software policy on the machine page: what to send to cma/softwaredrift and how to show what comes back.

// One entry per gap the comparison can report: what to call it, how serious it looks, and what it means.
export const GAPS = {
  identityMismatch: { label: 'Wrong device id', color: 'error', rank: 0, meaning: 'The device id belongs to a different computer, so the rule sets shown may be wrong.' },
  'forbidden-present': { label: 'Forbidden', color: 'error', rank: 1, meaning: 'The policy says this program must not be installed here.' },
  missing: { label: 'Missing', color: 'error', rank: 2, meaning: 'The policy requires this program and it is not installed.' },
  outdated: { label: 'Outdated', color: 'warning', rank: 3, meaning: 'winget knows a newer version.' },
  vulnerable: { label: 'Vulnerable', color: 'error', rank: 3.5, meaning: 'No rule mentions this program, but it has a known CVE scoring 7 or more.' },
  unverified: { label: 'Unverified', color: 'default', rank: 4, meaning: 'Installed, but nothing can say whether it is current.' },
  conflict: { label: 'Rules disagree', color: 'warning', rank: 5, meaning: 'Two rules at the same level disagree; forbidden won.' },
  noDefinition: { label: 'No definition', color: 'default', rank: 6, meaning: 'The policy names a program that has no definition yet.' },
  invalidPolicy: { label: 'Bad rule', color: 'default', rank: 7, meaning: 'A policy rule has an unknown mode.' },
};

export const gapInfo = (gap) => GAPS[gap] || { label: gap || '-', color: 'default', rank: 99, meaning: '' };

export const MODE_LABEL = { required: 'Required', allowed: 'Allowed', forbidden: 'Forbidden', 'n/a': '' };

// The comparison needs only the program list and the winget section; the CVE detail can be large and is left out.
export const driftBody = (data) => ({
  computerName: data?.computerName || '',
  programs: Array.isArray(data?.programs) ? data.programs : [],
  winget: data?.winget,
});

export const driftUrl = (account, deviceId) => {
  const q = new URLSearchParams({ account });
  if (deviceId) q.set('deviceId', deviceId);
  return `cma/softwaredrift?${q.toString()}`;
};

// The "cve" section the Software tab reads, from the shared comparison answer. While the answer is on its way, or if it
// failed, the section says so (the Software tab shows its status).
export const cveOf = (drift) => {
  if (drift?.state === 'ok') return drift.result?.cve || { status: 'error', message: 'The answer had no CVE data.' };
  if (drift?.state === 'error') return { status: 'error', message: drift.error };
  return { status: 'loading', message: 'Looking up the known CVEs...' };
};

// "3 CVEs, up to 8.8" for a finding whose program has known CVEs, else "".
export const cveText = (finding) => {
  const c = finding?.cve;
  if (!c?.count) return '';
  const score = c.highestScore === null || typeof c.highestScore === 'undefined' ? '' : `, up to ${c.highestScore}`;
  return `${c.count} ${c.count === 1 ? 'CVE' : 'CVEs'}${score}`;
};

// What to tell the person when the comparison could not be made.
export const driftErrorText = (ex) => {
  const status = ex?.response?.status;
  if (status === 404) return 'The software comparison is not available (the API route was not found).';
  if (status === 403) return 'You are not allowed to see the software policy for this account.';
  if (status === 413) return 'This result is too large to compare.';
  return ex?.response?.data?.error?.message || ex?.toString?.() || 'The software policy comparison failed';
};

// Findings worst first, then by program.
export const sortedFindings = (findings) => [...(Array.isArray(findings) ? findings : [])]
  .sort((a, b) => gapInfo(a.gap).rank - gapInfo(b.gap).rank || String(a.key).localeCompare(String(b.key)));

// "1.2.3 → 1.3.0" (installed, then the newest in the winget catalog), "newest 1.3.0" for a program that is not installed, "1.2.3" when
// the newest is not known, or "".
export const versionText = (finding) => {
  if (finding?.installed && finding?.available && finding.installed !== finding.available) return `${finding.installed} → ${finding.available}`;
  if (!finding?.installed && finding?.available) return `newest ${finding.available}`;
  return finding?.installed || '';
};

// The rule that produced the finding: "Required (account is myorg, software is developer)": the filter of the rule set that decided.
export const ruleText = (finding) => {
  const mode = MODE_LABEL[finding?.mode];
  if (!mode) return '';
  const by = finding?.decidedBy ? ` (${finding.decidedBy})` : '';
  return `${mode}${by}`;
};
